/* eslint-disable typebox-snake-case/typebox-object-keys-snake-case -- camelCase keys match existing DB/API schema */
import { Static, Type } from '@sinclair/typebox';
import { PictureSchema, withThumborQuality } from '../image';
import { BUILDER_SLOT, ComponentMeta } from './BuilderTypes';
import { COMPONENT_IDS } from './WebsiteTypes';
import { WebsiteFormFieldSchema } from './WebsiteFormField';
import { FOOTER_MAX_ITEMS, NEWS_HIGHLIGHT_MAX_ITEMS } from '../professional_constants';

// Size presets used across component image fields. Keep in sync with the CSS
// breakpoints in shared-components so `<img sizes>` attributes match reality.
//
// Qualité : `withThumborQuality` force q76 sur les images de CONTENU volumineuses
// (hero plein écran, cartes prestations/galerie) — cf. `IMG_CONTENT_QUALITY` dans
// image.ts. Les petits visuels (logo, avatar, thumbnails) restent en qualité par
// défaut (gain nul, bust de cache Thumbor inutile).
const HERO_SIZES = {
  sm: withThumborQuality('400x0'),
  md: withThumborQuality('800x0'),
  lg: withThumborQuality('1200x0'),
  xl: withThumborQuality('1920x0'),
};
const LOGO_SIZES = { sm: '120x0', md: '240x0' };
const AVATAR_SIZES = { sm: '80x0', md: '160x0', lg: '240x0' };
const MEDIUM_SIZES = {
  sm: withThumborQuality('400x0'),
  md: withThumborQuality('800x0'),
  lg: withThumborQuality('1200x0'),
};
const SMALL_SIZES = { sm: '200x0', md: '400x0' };

// ─── Header Hero ──────────────────────────────────────────────────────

export const HeroSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_MAIN_TITLE',
      placeholder: 'WEBSITE_BUILDER_MAIN_TITLE_PLACEHOLDER',
    }),
  ),
  subtitle: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_MAIN_SUBTITLE',
      placeholder: 'WEBSITE_BUILDER_MAIN_SUBTITLE_PLACEHOLDER',
    }),
  ),
  imageUrl: Type.Optional(
    PictureSchema(HERO_SIZES, {
      title: 'WEBSITE_BUILDER_HERO_IMAGE',
      description: "URL de l'image principale",
      // Force a fixed-ratio crop at upload, matching the hero's CSS display
      // ratio (16/9 in header-hero/style.scss) so the stored image already has
      // the right shape instead of relying on object-fit to crop at render.
      additionalMeta: { crop_ratio: 16 / 9 },
    }),
  ),
  imagesUrl: Type.Optional(
    Type.Array(
      PictureSchema(HERO_SIZES, {
        title: 'WEBSITE_BUILDER_HERO_IMAGE',
      }),
      {
        title: 'WEBSITE_BUILDER_HERO_IMAGES',
        description: 'Images du carousel',
        // Array-level `picture`/`multiple` drive the DynamicForm renderer to a
        // single multi-upload ImageUploadInput; each item carries `meta.picture`
        // (from PictureSchema) so transformToSchema signs every size at read.
        // `crop_ratio` forces a fixed 16/9 crop at upload, matching the hero's
        // CSS display ratio so every carousel slide is the same shape.
        meta: { picture: true, multiple: true, crop_ratio: 16 / 9 },
        minItems: 1,
        // Plafond large (garde-fou perf/LCP uniquement) : un hero lié à
        // `{{pro:pictures}}` peut déjà dépasser 20 slides (le binding stocke un
        // placeholder à 1 élément, donc maxItems ne s'applique pas), et les
        // uploads manuels doivent pouvoir suivre le même volume.
        maxItems: 100,
      },
    ),
  ),
  videoRef: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_HERO_VIDEO',
      placeholder: 'WEBSITE_BUILDER_VOD_PLAYER_VIDEO_REF_PLACEHOLDER',
      meta: { input_type: 'video_ref' },
    }),
  ),
  videoOverlay: Type.Optional(
    Type.Union(
      [
        Type.Literal('none', { label: 'WEBSITE_BUILDER_HERO_VIDEO_OVERLAY_NONE' }),
        Type.Literal('light', { label: 'WEBSITE_BUILDER_HERO_VIDEO_OVERLAY_LIGHT' }),
        Type.Literal('medium', { label: 'WEBSITE_BUILDER_HERO_VIDEO_OVERLAY_MEDIUM' }),
        Type.Literal('strong', { label: 'WEBSITE_BUILDER_HERO_VIDEO_OVERLAY_STRONG' }),
      ],
      {
        title: 'WEBSITE_BUILDER_HERO_VIDEO_OVERLAY',
        default: 'medium',
        meta: { multiple: false },
      },
    ),
  ),
});

export type HeroData = Static<typeof HeroSchema>;
export type HeroVariant = 'horizontal' | 'vertical' | 'carousel' | 'video';

export const HeaderHeroMeta: ComponentMeta<typeof HeroSchema, HeroVariant, keyof HeroData & string> = {
  id: COMPONENT_IDS.HEADER_HERO,
  version: '1.0.0',
  schema: HeroSchema,
  movable: true,
  allowMultipleAdd: false,
  variants: {
    horizontal: {
      label: 'WEBSITE_BUILDER_VARIANT_HORIZONTAL',
      required: ['title'],
      optional: ['subtitle'],
      hidden: ['imagesUrl', 'videoRef', 'videoOverlay'],
    },
    vertical: {
      label: 'WEBSITE_BUILDER_VARIANT_VERTICAL',
      required: ['title'],
      optional: ['subtitle'],
      hidden: ['imagesUrl', 'videoRef', 'videoOverlay'],
    },
    carousel: {
      label: 'WEBSITE_BUILDER_VARIANT_CAROUSEL',
      required: ['title', 'imagesUrl'],
      optional: ['subtitle'],
      hidden: ['imageUrl', 'videoRef', 'videoOverlay'],
    },
    video: {
      label: 'WEBSITE_BUILDER_VARIANT_VIDEO',
      required: ['title', 'videoRef'],
      optional: ['subtitle', 'videoOverlay'],
      hidden: ['imageUrl', 'imagesUrl'],
    },
  },
};

// ─── Header Menu ──────────────────────────────────────────────────────

export const HeaderMenuSchema = Type.Object({
  logoUrl: Type.Optional(
    PictureSchema(LOGO_SIZES, {
      title: 'WEBSITE_BUILDER_HEADER_LOGO',
      description: 'URL du logo affiché dans le header',
    }),
  ),
  logoSize: Type.Optional(
    Type.Union(
      [
        Type.Literal('small', { label: 'WEBSITE_BUILDER_HEADER_LOGO_SIZE_SMALL' }),
        Type.Literal('medium', { label: 'WEBSITE_BUILDER_HEADER_LOGO_SIZE_MEDIUM' }),
        Type.Literal('large', { label: 'WEBSITE_BUILDER_HEADER_LOGO_SIZE_LARGE' }),
      ],
      {
        title: 'WEBSITE_BUILDER_HEADER_LOGO_SIZE',
        default: 'medium',
        meta: { multiple: false },
      },
    ),
  ),
  ctaPageId: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_HEADER_CTA_PAGE',
      description: 'Page mise en avant à côté du menu hamburger',
      meta: { input_type: 'page_selector' },
    }),
  ),
  // Langues de traduction proposées dans le sélecteur du header (Google Traduction).
  // Le français reste la langue source ; ce champ ne liste que les traductions.
  // Tableau vide / absent → pas de sélecteur de langue affiché.
  languages: Type.Optional(
    Type.Array(Type.String(), {
      title: 'WEBSITE_BUILDER_HEADER_LANGUAGES',
      description:
        'Langues de traduction proposées aux visiteurs via Google Traduction. Le français reste la langue par défaut.',
      placeholder: 'WEBSITE_BUILDER_HEADER_LANGUAGES_PLACEHOLDER',
      meta: { input_type: 'language_selector' },
    }),
  ),
});

export type HeaderMenuData = Static<typeof HeaderMenuSchema>;
export type HeaderMenuVariant = 'classic' | 'hamburger';

export const HeaderMenuMeta: ComponentMeta<typeof HeaderMenuSchema, HeaderMenuVariant, keyof HeaderMenuData & string> =
  {
    id: COMPONENT_IDS.HEADER_MENU,
    version: '1.0.0',
    schema: HeaderMenuSchema,
    movable: false,
    allowMultipleAdd: false,
    global: true,
    variants: {
      classic: {
        label: 'WEBSITE_BUILDER_VARIANT_CLASSIC',
        required: ['logoUrl'],
        optional: ['logoSize', 'ctaPageId', 'languages'],
        hidden: [],
      },
      hamburger: {
        label: 'WEBSITE_BUILDER_VARIANT_HAMBURGER',
        required: ['logoUrl'],
        optional: ['logoSize', 'ctaPageId', 'languages'],
        hidden: [],
      },
    },
  };

// ─── Promotion liée (bandeau haut, section Promotion) ─────────────────

/** Sélecteur de promotion du formulaire : promotions mises en avant (site) ; sans ce mode, celui des campagnes. */
export enum ProPromoCodeSelectorMode {
  HIGHLIGHTED = 'HIGHLIGHTED',
}

/**
 * « Promotion liée » : une promotion mise en avant sur le site, dont le contenu (message, titre, code,
 * compte à rebours, bouton) remplit les champs laissés vides. Le bloc disparaît du site public quand elle
 * n'y est plus annoncée (terminée, en pause, quota épuisé).
 */
export const LINKED_PROMOTION_FIELD = 'linkedPromotionId';

const LinkedPromotionIdField = Type.Optional(
  Type.Number({
    title: 'WEBSITE_BUILDER_LINKED_PROMOTION',
    placeholder: 'WEBSITE_BUILDER_LINKED_PROMOTION_PLACEHOLDER',
    meta: { input_type: 'pro_promo_code_selector', promo_selector: ProPromoCodeSelectorMode.HIGHLIGHTED },
  }),
);

// ─── Top Header ───────────────────────────────────────────────────────

export const TopHeaderSchema = Type.Object({
  linkedPromotionId: LinkedPromotionIdField,
  // Requis par les variantes, sauf avec une promotion liée (`requiredUnlessSet`).
  message: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TOP_HEADER_MESSAGE',
      placeholder: 'WEBSITE_BUILDER_TOP_HEADER_MESSAGE_PLACEHOLDER',
      maxLength: 160,
    }),
  ),
  countdownDate: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TOP_HEADER_COUNTDOWN_DATE',
      format: 'date-time',
      meta: { input_type: 'date' },
    }),
  ),
  ctaLabel: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TOP_HEADER_CTA_LABEL',
      placeholder: 'WEBSITE_BUILDER_TOP_HEADER_CTA_LABEL_PLACEHOLDER',
      maxLength: 40,
    }),
  ),
  ctaHref: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TOP_HEADER_CTA_HREF',
      meta: { field_type: 'link' },
    }),
  ),
  dismissible: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_TOP_HEADER_DISMISSIBLE',
      default: false,
    }),
  ),
});

export type TopHeaderData = Static<typeof TopHeaderSchema>;
export type TopHeaderVariant = 'simple' | 'countdown' | 'promo';

export const TopHeaderMeta: ComponentMeta<typeof TopHeaderSchema, TopHeaderVariant, keyof TopHeaderData & string> = {
  id: COMPONENT_IDS.TOP_HEADER,
  version: '1.0.0',
  schema: TopHeaderSchema,
  movable: false,
  allowMultipleAdd: false,
  global: true,
  slots: [BUILDER_SLOT.TOP_HEADER],
  // Promotion liée : message, fin du compte à rebours et bouton viennent de la promotion.
  requiredUnlessSet: { field: LINKED_PROMOTION_FIELD, fields: ['message', 'countdownDate', 'ctaLabel', 'ctaHref'] },
  variants: {
    simple: {
      label: 'WEBSITE_BUILDER_VARIANT_TOP_HEADER_SIMPLE',
      required: ['message'],
      optional: [LINKED_PROMOTION_FIELD, 'ctaLabel', 'ctaHref', 'dismissible'],
      hidden: ['countdownDate'],
    },
    countdown: {
      label: 'WEBSITE_BUILDER_VARIANT_TOP_HEADER_COUNTDOWN',
      required: ['message', 'countdownDate'],
      optional: [LINKED_PROMOTION_FIELD, 'ctaLabel', 'ctaHref', 'dismissible'],
      hidden: [],
    },
    promo: {
      label: 'WEBSITE_BUILDER_VARIANT_TOP_HEADER_PROMO',
      required: ['message', 'ctaLabel', 'ctaHref'],
      optional: [LINKED_PROMOTION_FIELD, 'dismissible'],
      hidden: ['countdownDate'],
    },
  },
};

// ─── Floating Contact (bouton WhatsApp / téléphone flottant) ───────────

export const FloatingContactSchema = Type.Object({
  phoneNumber: Type.String({
    title: 'WEBSITE_BUILDER_FLOATING_CONTACT_PHONE',
    placeholder: 'WEBSITE_BUILDER_FLOATING_CONTACT_PHONE_PLACEHOLDER',
    // `binding_keys` restreint le sélecteur de binding à ce(s) seul(s) champ(s) pro :
    // un bouton « Appeler / WhatsApp » n'a de sens que lié au téléphone, pas au nom/SIRET.
    meta: { input_type: 'phone_number', binding_keys: ['pro:phone'] },
  }),
});

export type FloatingContactData = Static<typeof FloatingContactSchema>;
// Le canal est porté par le variant (idiomatique builder) : WhatsApp ou appel.
export type FloatingContactVariant = 'whatsapp' | 'phone';

export const FloatingContactMeta: ComponentMeta<
  typeof FloatingContactSchema,
  FloatingContactVariant,
  keyof FloatingContactData & string
> = {
  id: COMPONENT_IDS.FLOATING_CONTACT,
  version: '1.0.0',
  schema: FloatingContactSchema,
  movable: false,
  allowMultipleAdd: false,
  global: true,
  floating: true,
  variants: {
    whatsapp: {
      label: 'WEBSITE_BUILDER_VARIANT_FLOATING_CONTACT_WHATSAPP',
      required: ['phoneNumber'],
      optional: [],
      hidden: [],
    },
    phone: {
      label: 'WEBSITE_BUILDER_VARIANT_FLOATING_CONTACT_PHONE',
      required: ['phoneNumber'],
      optional: [],
      hidden: [],
    },
  },
};

// ─── Footer ───────────────────────────────────────────────────────────

/* Repris côté runtime ET côté SSR (FOOTER_ITEMS_LIMIT dans shared-components) et dans
   les libellés fr-FR ; déclaré dans professional_constants pour être importable partout. */
export { FOOTER_MAX_ITEMS, NEWS_HIGHLIGHT_MAX_ITEMS };

export const FooterSchema = Type.Object({
  logoUrl: Type.Optional(
    PictureSchema(LOGO_SIZES, {
      title: 'WEBSITE_BUILDER_FOOTER_LOGO',
      description: 'URL du logo affiché dans le footer',
    }),
  ),
  showNavLinks: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_FOOTER_SHOW_NAV',
      default: true,
    }),
  ),
  /* Sélection manuelle des colonnes du footer. Laisser vide = comportement historique
     (les 6 premiers éléments : pages du menu, prestations du catalogue, derniers articles). */
  navPageIds: Type.Optional(
    Type.Array(
      Type.String({
        meta: { input_type: 'page_selector' },
      }),
      {
        title: 'WEBSITE_BUILDER_FOOTER_NAV_PAGES',
        placeholder: 'WEBSITE_BUILDER_FOOTER_NAV_PAGES_PLACEHOLDER',
        meta: { input_type: 'page_selector', visible_when: { field: 'showNavLinks', value: true } },
        maxItems: FOOTER_MAX_ITEMS,
      },
    ),
  ),
  showSocialLinks: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_FOOTER_SHOW_SOCIAL',
      default: true,
    }),
  ),
  showLatestArticles: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_FOOTER_SHOW_ARTICLES',
      default: false,
    }),
  ),
  latestArticleIds: Type.Optional(
    Type.Array(
      Type.String({
        meta: { input_type: 'news_selector' },
      }),
      {
        title: 'WEBSITE_BUILDER_FOOTER_ARTICLES_SELECTION',
        placeholder: 'WEBSITE_BUILDER_FOOTER_ARTICLES_SELECTION_PLACEHOLDER',
        meta: { input_type: 'news_selector', visible_when: { field: 'showLatestArticles', value: true } },
        maxItems: FOOTER_MAX_ITEMS,
      },
    ),
  ),
  showFeaturedServices: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_FOOTER_SHOW_SERVICES',
      default: false,
    }),
  ),
  featuredServiceIds: Type.Optional(
    Type.Array(
      Type.String({
        meta: { input_type: 'prestation_selector' },
      }),
      {
        title: 'WEBSITE_BUILDER_FOOTER_SERVICES_SELECTION',
        placeholder: 'WEBSITE_BUILDER_FOOTER_SERVICES_SELECTION_PLACEHOLDER',
        meta: { input_type: 'prestation_selector', visible_when: { field: 'showFeaturedServices', value: true } },
        maxItems: FOOTER_MAX_ITEMS,
      },
    ),
  ),
  proName: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_FOOTER_PRO_NAME',
      default: '{{pro:name}}',
    }),
  ),
});

export type FooterData = Static<typeof FooterSchema>;
export type FooterVariant = 'classic' | 'minimal' | 'centered';

export const FooterMeta: ComponentMeta<typeof FooterSchema, FooterVariant, keyof FooterData & string> = {
  id: COMPONENT_IDS.FOOTER,
  version: '1.0.0',
  schema: FooterSchema,
  movable: false,
  allowMultipleAdd: false,
  global: true,
  variants: {
    classic: {
      label: 'WEBSITE_BUILDER_VARIANT_FOOTER_CLASSIC',
      required: ['logoUrl'],
      optional: [
        'showNavLinks',
        'navPageIds',
        'showSocialLinks',
        'showLatestArticles',
        'latestArticleIds',
        'showFeaturedServices',
        'featuredServiceIds',
      ],
      hidden: ['proName'],
    },
    minimal: {
      label: 'WEBSITE_BUILDER_VARIANT_FOOTER_MINIMAL',
      required: ['logoUrl'],
      optional: ['showSocialLinks'],
      hidden: [
        'proName',
        'showNavLinks',
        'navPageIds',
        'showLatestArticles',
        'latestArticleIds',
        'showFeaturedServices',
        'featuredServiceIds',
      ],
    },
    centered: {
      label: 'WEBSITE_BUILDER_VARIANT_FOOTER_CENTERED',
      required: ['logoUrl'],
      optional: [
        'showNavLinks',
        'navPageIds',
        'showSocialLinks',
        'showLatestArticles',
        'latestArticleIds',
        'showFeaturedServices',
        'featuredServiceIds',
      ],
      hidden: ['proName'],
    },
  },
};

// ─── Call To Action ───────────────────────────────────────────────────

export const CallToActionSchema = Type.Object({
  text: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CTA_TEXT',
    }),
  ),
  primaryLabel: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CTA_PRIMARY_LABEL',
    }),
  ),
  primaryHref: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CTA_PRIMARY_HREF',
      meta: { field_type: 'link' },
    }),
  ),
  secondaryLabel: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CTA_SECONDARY_LABEL',
    }),
  ),
  secondaryHref: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CTA_SECONDARY_HREF',
      meta: { field_type: 'link' },
    }),
  ),
});

export type CallToActionData = Static<typeof CallToActionSchema>;
export type CallToActionVariant = 'single' | 'double';

export const CallToActionMeta: ComponentMeta<
  typeof CallToActionSchema,
  CallToActionVariant,
  keyof CallToActionData & string
> = {
  id: COMPONENT_IDS.CALL_TO_ACTION,
  version: '1.0.0',
  schema: CallToActionSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    single: {
      label: 'WEBSITE_BUILDER_VARIANT_CTA_SINGLE',
      required: ['primaryLabel', 'primaryHref'],
      optional: ['text'],
      hidden: ['secondaryLabel', 'secondaryHref'],
    },
    double: {
      label: 'WEBSITE_BUILDER_VARIANT_CTA_DOUBLE',
      required: ['primaryLabel', 'primaryHref', 'secondaryLabel', 'secondaryHref'],
      optional: ['text'],
      hidden: [],
    },
  },
};

// ─── FAQ ──────────────────────────────────────────────────────────────

export const FaqSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_FAQ_TITLE',
      placeholder: 'WEBSITE_BUILDER_FAQ_TITLE_PLACEHOLDER',
    }),
  ),
  items: Type.Optional(
    Type.Array(
      Type.Object({
        question: Type.String({
          title: 'WEBSITE_BUILDER_FAQ_QUESTION',
          placeholder: 'WEBSITE_BUILDER_FAQ_QUESTION_PLACEHOLDER',
        }),
        answer: Type.String({
          title: 'WEBSITE_BUILDER_FAQ_ANSWER',
          placeholder: 'WEBSITE_BUILDER_FAQ_ANSWER_PLACEHOLDER',
        }),
      }),
      {
        title: 'WEBSITE_BUILDER_FAQ_ITEMS',
        minItems: 1,
      },
    ),
  ),
  allowMultipleOpen: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_FAQ_ALLOW_MULTIPLE_OPEN',
      default: false,
    }),
  ),
});

export type FaqData = Static<typeof FaqSchema>;
export type FaqVariant = 'collapsible' | 'open';

export const FaqMeta: ComponentMeta<typeof FaqSchema, FaqVariant, keyof FaqData & string> = {
  id: COMPONENT_IDS.FAQ,
  version: '1.0.0',
  schema: FaqSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    collapsible: {
      label: 'WEBSITE_BUILDER_VARIANT_FAQ_COLLAPSIBLE',
      required: ['items'],
      optional: ['title', 'allowMultipleOpen'],
      hidden: [],
    },
    open: {
      label: 'WEBSITE_BUILDER_VARIANT_FAQ_OPEN',
      required: ['items'],
      optional: ['title'],
      hidden: ['allowMultipleOpen'],
    },
  },
};

// ─── Newsletter Subscription ──────────────────────────────────────────

export const NewsletterSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_NEWSLETTER_TITLE',
    }),
  ),
  description: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_NEWSLETTER_DESCRIPTION',
    }),
  ),
  buttonLabel: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_NEWSLETTER_BUTTON_LABEL',
    }),
  ),
  consentText: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_NEWSLETTER_CONSENT',
    }),
  ),
  tagId: Type.Optional(
    Type.Array(Type.Number(), {
      minItems: 0,
      maxItems: 1,
      meta: { input_type: 'pro_tag_selector' },
      placeholder: 'INPUT_PLACEHOLDER_CHOOSE',
      title: 'WEBSITE_BUILDER_NEWSLETTER_TAG',
    }),
  ),
  successMessage: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_NEWSLETTER_SUCCESS_MESSAGE',
    }),
  ),
});

export type NewsletterData = Static<typeof NewsletterSchema>;
export type NewsletterVariant = 'horizontal' | 'minimal';

export const NewsletterMeta: ComponentMeta<typeof NewsletterSchema, NewsletterVariant, keyof NewsletterData & string> =
  {
    id: COMPONENT_IDS.NEWSLETTER_SUBSCRIPTION,
    version: '1.0.0',
    schema: NewsletterSchema,
    movable: true,
    allowMultipleAdd: true,
    variants: {
      horizontal: {
        label: 'WEBSITE_BUILDER_VARIANT_HORIZONTAL',
        required: ['title', 'buttonLabel', 'consentText'],
        optional: ['description', 'tagId', 'successMessage'],
        hidden: [],
      },
      minimal: {
        label: 'WEBSITE_BUILDER_VARIANT_NEWSLETTER_MINIMAL',
        required: ['title', 'buttonLabel', 'consentText'],
        optional: ['tagId', 'successMessage'],
        hidden: ['description'],
      },
    },
  };

// ─── Contact Form ─────────────────────────────────────────────────────
// Formulaire de contact dont le pro choisit les champs depuis le builder
// (`fields`, édité via l'éditeur `form_fields_editor`). À la soumission, un POST
// public part vers `/public/professional/:slug/contact-submission` (persistance +
// email + notif in-app au pro).

export const ContactFormSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CONTACT_FORM_TITLE',
    }),
  ),
  description: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CONTACT_FORM_DESCRIPTION',
    }),
  ),
  fields: Type.Optional(
    Type.Array(WebsiteFormFieldSchema, {
      meta: { input_type: 'form_fields_editor' },
      title: 'WEBSITE_BUILDER_CONTACT_FORM_FIELDS',
    }),
  ),
  buttonLabel: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CONTACT_FORM_BUTTON_LABEL',
    }),
  ),
  consentText: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CONTACT_FORM_CONSENT',
    }),
  ),
  successMessage: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CONTACT_FORM_SUCCESS_MESSAGE',
    }),
  ),
});

export type ContactFormData = Static<typeof ContactFormSchema>;
export type ContactFormVariant = 'card' | 'plain';

export const ContactFormMeta: ComponentMeta<
  typeof ContactFormSchema,
  ContactFormVariant,
  keyof ContactFormData & string
> = {
  id: COMPONENT_IDS.CONTACT_FORM,
  version: '1.0.0',
  schema: ContactFormSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    card: {
      label: 'WEBSITE_BUILDER_VARIANT_CONTACT_FORM_CARD',
      required: ['title', 'fields', 'buttonLabel', 'consentText'],
      optional: ['description', 'successMessage'],
      hidden: [],
    },
    plain: {
      label: 'WEBSITE_BUILDER_VARIANT_CONTACT_FORM_PLAIN',
      required: ['title', 'fields', 'buttonLabel', 'consentText'],
      optional: ['description', 'successMessage'],
      hidden: [],
    },
  },
};

// ─── Callback Request ─────────────────────────────────────────────────
// Variante « demande de rappel » : même moteur que le formulaire de contact, mais
// orienté téléphone (le pro rappelle le visiteur). Bloc distinct pour apparaître
// séparément dans le picker, avec ses propres défauts (nom + téléphone).

export const CallbackRequestSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CALLBACK_REQUEST_TITLE',
    }),
  ),
  description: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CALLBACK_REQUEST_DESCRIPTION',
    }),
  ),
  fields: Type.Optional(
    Type.Array(WebsiteFormFieldSchema, {
      meta: { input_type: 'form_fields_editor' },
      title: 'WEBSITE_BUILDER_CALLBACK_REQUEST_FIELDS',
    }),
  ),
  buttonLabel: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CALLBACK_REQUEST_BUTTON_LABEL',
    }),
  ),
  consentText: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CALLBACK_REQUEST_CONSENT',
    }),
  ),
  successMessage: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CALLBACK_REQUEST_SUCCESS_MESSAGE',
    }),
  ),
});

export type CallbackRequestData = Static<typeof CallbackRequestSchema>;
export type CallbackRequestVariant = 'card' | 'plain';

export const CallbackRequestMeta: ComponentMeta<
  typeof CallbackRequestSchema,
  CallbackRequestVariant,
  keyof CallbackRequestData & string
> = {
  id: COMPONENT_IDS.CALLBACK_REQUEST,
  version: '1.0.0',
  schema: CallbackRequestSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    card: {
      label: 'WEBSITE_BUILDER_VARIANT_CONTACT_FORM_CARD',
      required: ['title', 'fields', 'buttonLabel', 'consentText'],
      optional: ['description', 'successMessage'],
      hidden: [],
    },
    plain: {
      label: 'WEBSITE_BUILDER_VARIANT_CONTACT_FORM_PLAIN',
      required: ['title', 'fields', 'buttonLabel', 'consentText'],
      optional: ['description', 'successMessage'],
      hidden: [],
    },
  },
};

// ─── Prestation Listing ───────────────────────────────────────────────

export const PrestationListingSchema = Type.Object({
  categoryFilter: Type.Optional(
    Type.Array(
      Type.String({
        meta: { input_type: 'catalog_category_selector' },
      }),
      {
        title: 'WEBSITE_BUILDER_PRESTATION_CATEGORY_FILTER',
        meta: { input_type: 'catalog_category_selector' },
      },
    ),
  ),
});

export type PrestationListingData = Static<typeof PrestationListingSchema>;
export type PrestationListingVariant = 'grid' | 'list';

export const PrestationListingMeta: ComponentMeta<
  typeof PrestationListingSchema,
  PrestationListingVariant,
  keyof PrestationListingData & string
> = {
  id: COMPONENT_IDS.PRESTATION_LISTING,
  version: '1.0.0',
  schema: PrestationListingSchema,
  movable: true,
  allowMultipleAdd: false,
  variants: {
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_GRID',
      required: [],
      optional: ['categoryFilter'],
      hidden: [],
    },
    list: {
      label: 'WEBSITE_BUILDER_VARIANT_LIST',
      required: [],
      optional: ['categoryFilter'],
      hidden: [],
    },
  },
};

// ─── Prestation Detail ────────────────────────────────────────────────

export const PrestationDetailSchema = Type.Object({});

export type PrestationDetailData = Static<typeof PrestationDetailSchema>;
export type PrestationDetailVariant = 'default' | 'sidebar';

export const PrestationDetailMeta: ComponentMeta<
  typeof PrestationDetailSchema,
  PrestationDetailVariant,
  keyof PrestationDetailData & string
> = {
  id: COMPONENT_IDS.PRESTATION_DETAIL,
  version: '1.0.0',
  schema: PrestationDetailSchema,
  movable: false,
  allowMultipleAdd: false,
  variants: {
    default: {
      label: 'WEBSITE_BUILDER_VARIANT_DEFAULT',
      required: [],
      optional: [],
      hidden: [],
    },
    sidebar: {
      label: 'WEBSITE_BUILDER_VARIANT_SIDEBAR',
      required: [],
      optional: [],
      hidden: [],
    },
  },
};

// ─── News Listing ────────────────────────────────────────────────────

export const NewsListingSchema = Type.Object({
  showCategorySidebar: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_NEWS_SHOW_SIDEBAR',
      default: true,
    }),
  ),
});

export type NewsListingData = Static<typeof NewsListingSchema>;
export type NewsListingVariant = 'grid' | 'list';

export const NewsListingMeta: ComponentMeta<
  typeof NewsListingSchema,
  NewsListingVariant,
  keyof NewsListingData & string
> = {
  id: COMPONENT_IDS.NEWS_LISTING,
  version: '1.0.0',
  schema: NewsListingSchema,
  movable: true,
  allowMultipleAdd: false,
  variants: {
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_GRID',
      required: [],
      optional: ['showCategorySidebar'],
      hidden: [],
    },
    list: {
      label: 'WEBSITE_BUILDER_VARIANT_LIST',
      required: [],
      optional: ['showCategorySidebar'],
      hidden: [],
    },
  },
};

// ─── News Highlight ──────────────────────────────────────────────────
// Bloc « Actualités à la une » (accueil, à-propos, page libre…) : même convention que
// le footer et les prestations mises en avant — sélection manuelle vide = automatique
// (les N derniers articles publiés, éventuellement restreints à une thématique).

export const NewsHighlightSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_NEWS_HIGHLIGHT_TITLE',
    }),
  ),
  newsFilter: Type.Optional(
    Type.Array(
      Type.String({
        meta: { input_type: 'news_selector' },
      }),
      {
        title: 'WEBSITE_BUILDER_NEWS_HIGHLIGHT_FILTER',
        placeholder: 'WEBSITE_BUILDER_NEWS_HIGHLIGHT_FILTER_PLACEHOLDER',
        meta: { input_type: 'news_selector' },
        maxItems: NEWS_HIGHLIGHT_MAX_ITEMS,
      },
    ),
  ),
  // Thématique (id de tag) appliquée au mode automatique uniquement : une sélection
  // manuelle est affichée telle quelle.
  tagFilter: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_NEWS_HIGHLIGHT_TAG',
      placeholder: 'WEBSITE_BUILDER_NEWS_HIGHLIGHT_TAG_PLACEHOLDER',
      meta: { input_type: 'pro_news_tag_selector' },
    }),
  ),
  showAllLink: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_NEWS_HIGHLIGHT_SHOW_ALL_LINK',
      default: true,
    }),
  ),
});

export type NewsHighlightData = Static<typeof NewsHighlightSchema>;
export type NewsHighlightVariant = 'grid' | 'list' | 'featured';

const NEWS_HIGHLIGHT_FIELDS: (keyof NewsHighlightData & string)[] = ['title', 'newsFilter', 'tagFilter', 'showAllLink'];

export const NewsHighlightMeta: ComponentMeta<
  typeof NewsHighlightSchema,
  NewsHighlightVariant,
  keyof NewsHighlightData & string
> = {
  id: COMPONENT_IDS.NEWS_HIGHLIGHT,
  version: '1.0.0',
  schema: NewsHighlightSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_GRID',
      required: [],
      optional: NEWS_HIGHLIGHT_FIELDS,
      hidden: [],
    },
    list: {
      label: 'WEBSITE_BUILDER_VARIANT_LIST',
      required: [],
      optional: NEWS_HIGHLIGHT_FIELDS,
      hidden: [],
    },
    featured: {
      label: 'WEBSITE_BUILDER_VARIANT_FEATURED',
      required: [],
      optional: NEWS_HIGHLIGHT_FIELDS,
      hidden: [],
    },
  },
};

// ─── Text Block ──────────────────────────────────────────────────────

export const TextBlockSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TEXT_BLOCK_TITLE',
    }),
  ),
  content: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TEXT_BLOCK_CONTENT',
      meta: {
        input_type: 'rich_text',
        allowed_tags: ['b', 'i', 'u', 'a', 'h2', 'h3', 'ul', 'ol', 'variable'],
      },
    }),
  ),
  contentRight: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TEXT_BLOCK_CONTENT_RIGHT',
      meta: {
        input_type: 'rich_text',
        allowed_tags: ['b', 'i', 'u', 'a', 'h2', 'h3', 'ul', 'ol', 'variable'],
      },
    }),
  ),
  imageUrl: Type.Optional(
    PictureSchema(MEDIUM_SIZES, {
      title: 'WEBSITE_BUILDER_TEXT_BLOCK_IMAGE',
    }),
  ),
  imagePosition: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TEXT_BLOCK_IMAGE_POSITION',
      default: 'right',
    }),
  ),
});

export type TextBlockData = Static<typeof TextBlockSchema>;
export type TextBlockVariant = 'single-column' | 'two-columns' | 'with-image';

export const TextBlockMeta: ComponentMeta<typeof TextBlockSchema, TextBlockVariant, keyof TextBlockData & string> = {
  id: COMPONENT_IDS.TEXT_BLOCK,
  version: '1.0.0',
  schema: TextBlockSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    'single-column': {
      label: 'WEBSITE_BUILDER_VARIANT_TEXT_SINGLE_COLUMN',
      required: [],
      optional: ['title', 'content'],
      hidden: ['contentRight', 'imageUrl', 'imagePosition'],
    },
    'two-columns': {
      label: 'WEBSITE_BUILDER_VARIANT_TEXT_TWO_COLUMNS',
      required: [],
      optional: ['title', 'content', 'contentRight'],
      hidden: ['imageUrl', 'imagePosition'],
    },
    'with-image': {
      label: 'WEBSITE_BUILDER_VARIANT_TEXT_WITH_IMAGE',
      required: [],
      optional: ['title', 'content', 'imageUrl', 'imagePosition'],
      hidden: ['contentRight'],
    },
  },
};

// ─── Team Listing ────────────────────────────────────────────────────

export const TeamListingSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TEAM_TITLE',
    }),
  ),
  showExpertise: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_TEAM_SHOW_EXPERTISE',
      default: true,
    }),
  ),
  showCareerPath: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_TEAM_SHOW_CAREER',
      default: false,
    }),
  ),
});

export type TeamListingData = Static<typeof TeamListingSchema>;
export type TeamListingVariant = 'carousel' | 'grid' | 'list';

export const TeamListingMeta: ComponentMeta<
  typeof TeamListingSchema,
  TeamListingVariant,
  keyof TeamListingData & string
> = {
  id: COMPONENT_IDS.TEAM_LISTING,
  version: '1.0.0',
  schema: TeamListingSchema,
  movable: true,
  allowMultipleAdd: false,
  variants: {
    carousel: {
      label: 'WEBSITE_BUILDER_VARIANT_TEAM_CAROUSEL',
      required: [],
      optional: ['title', 'showExpertise', 'showCareerPath'],
      hidden: [],
    },
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_TEAM_GRID',
      required: [],
      optional: ['title', 'showExpertise', 'showCareerPath'],
      hidden: [],
    },
    list: {
      label: 'WEBSITE_BUILDER_VARIANT_TEAM_LIST',
      required: [],
      optional: ['title', 'showExpertise'],
      hidden: ['showCareerPath'],
    },
  },
};

// ─── Contact Info ────────────────────────────────────────────────────

export const ContactInfoSchema = Type.Object({
  selectedPlaceIds: Type.Optional(
    Type.Array(Type.Number(), {
      title: 'WEBSITE_BUILDER_CONTACT_SELECTED_PLACES',
      placeholder: 'WEBSITE_BUILDER_CONTACT_SELECTED_PLACES',
      meta: { input_type: 'pro_place_selector', use_all_sentinel: true },
    }),
  ),
  travelInfo: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CONTACT_TRAVEL_INFO',
    }),
  ),
  showPhone: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_CONTACT_SHOW_PHONE',
      default: true,
    }),
  ),
  showEmail: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_CONTACT_SHOW_EMAIL',
      default: true,
    }),
  ),
  showCallback: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_CONTACT_SHOW_CALLBACK',
      default: true,
    }),
  ),
  showSocialLinks: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_CONTACT_SHOW_SOCIAL',
      default: true,
    }),
  ),
  // Champs SUPPLÉMENTAIRES du formulaire « Écrire un message » (ouvert en modale par le bouton email).
  // Les champs de base (nom/email/objet/message) sont toujours présents et verrouillés ;
  // `form_kind: 'contact'` indique à l'éditeur d'afficher cette base en lecture seule au-dessus.
  messageFormFields: Type.Optional(
    Type.Array(WebsiteFormFieldSchema, {
      meta: { input_type: 'form_fields_editor', form_kind: 'contact' },
      title: 'WEBSITE_BUILDER_CONTACT_MESSAGE_FIELDS',
    }),
  ),
  // Champs SUPPLÉMENTAIRES du formulaire « Être rappelé » (ouvert en modale par le bouton de rappel).
  // Les champs de base (nom/téléphone/message/créneau) sont toujours présents et verrouillés.
  callbackFormFields: Type.Optional(
    Type.Array(WebsiteFormFieldSchema, {
      meta: { input_type: 'form_fields_editor', form_kind: 'callback' },
      title: 'WEBSITE_BUILDER_CONTACT_CALLBACK_FIELDS',
    }),
  ),
});

export type ContactInfoData = Static<typeof ContactInfoSchema>;
export type ContactInfoVariant = 'map' | 'split-map' | 'compact';

export const ContactInfoMeta: ComponentMeta<
  typeof ContactInfoSchema,
  ContactInfoVariant,
  keyof ContactInfoData & string
> = {
  id: COMPONENT_IDS.CONTACT_INFO,
  version: '1.0.0',
  schema: ContactInfoSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    map: {
      label: 'WEBSITE_BUILDER_VARIANT_CONTACT_MAP',
      required: [],
      optional: [
        'selectedPlaceIds',
        'travelInfo',
        'showPhone',
        'showEmail',
        'showCallback',
        'showSocialLinks',
        'messageFormFields',
        'callbackFormFields',
      ],
      hidden: [],
    },
    'split-map': {
      label: 'WEBSITE_BUILDER_VARIANT_CONTACT_SPLIT_MAP',
      required: [],
      optional: [
        'selectedPlaceIds',
        'travelInfo',
        'showPhone',
        'showEmail',
        'showCallback',
        'showSocialLinks',
        'messageFormFields',
        'callbackFormFields',
      ],
      hidden: [],
    },
    compact: {
      label: 'WEBSITE_BUILDER_VARIANT_CONTACT_COMPACT',
      required: [],
      optional: [
        'selectedPlaceIds',
        'travelInfo',
        'showPhone',
        'showEmail',
        'showCallback',
        'showSocialLinks',
        'messageFormFields',
        'callbackFormFields',
      ],
      hidden: [],
    },
  },
};

// ─── Prestation Highlight ────────────────────────────────────────────

export const PrestationHighlightSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PRESTATION_HIGHLIGHT_TITLE',
    }),
  ),
  prestationFilter: Type.Optional(
    Type.Array(
      Type.String({
        meta: { input_type: 'prestation_selector' },
      }),
      {
        title: 'WEBSITE_BUILDER_PRESTATION_HIGHLIGHT_FILTER',
        meta: { input_type: 'prestation_selector' },
        maxItems: 3,
      },
    ),
  ),
});

export type PrestationHighlightData = Static<typeof PrestationHighlightSchema>;
export type PrestationHighlightVariant = 'grid' | 'list';

export const PrestationHighlightMeta: ComponentMeta<
  typeof PrestationHighlightSchema,
  PrestationHighlightVariant,
  keyof PrestationHighlightData & string
> = {
  id: COMPONENT_IDS.PRESTATION_HIGHLIGHT,
  version: '1.0.0',
  schema: PrestationHighlightSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_GRID',
      required: [],
      optional: ['title', 'prestationFilter'],
      hidden: [],
    },
    list: {
      label: 'WEBSITE_BUILDER_VARIANT_LIST',
      required: [],
      optional: ['title', 'prestationFilter'],
      hidden: [],
    },
  },
};

// ─── Booking Widget ─────────────────────────────────────────────────

export const BookingWidgetSchema = Type.Object({
  prestationIds: Type.Optional(
    Type.Array(
      Type.String({
        meta: { input_type: 'prestation_selector' },
      }),
      {
        title: 'WEBSITE_BUILDER_BOOKING_WIDGET_PRESTATION_FILTER',
        meta: { input_type: 'prestation_selector' },
      },
    ),
  ),
  categoryIds: Type.Optional(
    Type.Array(
      Type.String({
        meta: { input_type: 'catalog_category_selector' },
      }),
      {
        title: 'WEBSITE_BUILDER_BOOKING_WIDGET_CATEGORY_FILTER',
        meta: { input_type: 'catalog_category_selector' },
      },
    ),
  ),
  placeIds: Type.Optional(
    Type.Array(Type.Number(), {
      title: 'WEBSITE_BUILDER_BOOKING_WIDGET_PLACE_FILTER',
      meta: { input_type: 'pro_place_selector', use_all_sentinel: true },
    }),
  ),
  preselectIds: Type.Optional(
    Type.Array(
      Type.String({
        meta: { input_type: 'prestation_selector' },
      }),
      {
        title: 'WEBSITE_BUILDER_BOOKING_WIDGET_PRESELECT',
        meta: { input_type: 'prestation_selector' },
      },
    ),
  ),
});

export type BookingWidgetData = Static<typeof BookingWidgetSchema>;
export type BookingWidgetVariant = 'default';

export const BookingWidgetMeta: ComponentMeta<
  typeof BookingWidgetSchema,
  BookingWidgetVariant,
  keyof BookingWidgetData & string
> = {
  id: COMPONENT_IDS.BOOKING_WIDGET,
  version: '1.0.0',
  schema: BookingWidgetSchema,
  movable: true,
  allowMultipleAdd: false,
  variants: {
    default: {
      label: 'WEBSITE_BUILDER_VARIANT_DEFAULT',
      required: [],
      optional: ['prestationIds', 'categoryIds', 'placeIds', 'preselectIds'],
      hidden: [],
    },
  },
};

// ─── Gift card / pack purchase tunnel ────────────────────────────────
// Bloc « tunnel d'achat » (carte cadeau + forfait). Aucun champ configurable : le scope (complet
// vs forfait) est dérivé automatiquement du type de page hôte et des deep-links (?prestation/?pack).

export const GiftCardSchema = Type.Object({});

export type GiftCardData = Static<typeof GiftCardSchema>;
export type GiftCardVariant = 'default';

export const GiftCardMeta: ComponentMeta<typeof GiftCardSchema, GiftCardVariant, keyof GiftCardData & string> = {
  id: COMPONENT_IDS.GIFT_CARD,
  version: '1.0.0',
  schema: GiftCardSchema,
  movable: true,
  allowMultipleAdd: false,
  variants: {
    default: {
      label: 'WEBSITE_BUILDER_VARIANT_DEFAULT',
      required: [],
      optional: [],
      hidden: [],
    },
  },
};

// ─── Testimonials ────────────────────────────────────────────────────

export const TestimonialItemSchema = Type.Object({
  text: Type.String({
    title: 'WEBSITE_BUILDER_TESTIMONIAL_TEXT',
    placeholder: 'WEBSITE_BUILDER_TESTIMONIAL_TEXT_PLACEHOLDER',
    minLength: 1,
    maxLength: 500,
  }),
  authorName: Type.String({
    title: 'WEBSITE_BUILDER_TESTIMONIAL_AUTHOR_NAME',
    placeholder: 'WEBSITE_BUILDER_TESTIMONIAL_AUTHOR_NAME_PLACEHOLDER',
    maxLength: 100,
  }),
  authorRole: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TESTIMONIAL_AUTHOR_ROLE',
      placeholder: 'WEBSITE_BUILDER_TESTIMONIAL_AUTHOR_ROLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  authorPhotoUrl: Type.Optional(
    PictureSchema(AVATAR_SIZES, {
      title: 'WEBSITE_BUILDER_TESTIMONIAL_AUTHOR_PHOTO',
    }),
  ),
  videoRef: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TESTIMONIAL_VIDEO_REF',
      placeholder: 'WEBSITE_BUILDER_VOD_PLAYER_VIDEO_REF_PLACEHOLDER',
      meta: { input_type: 'video_ref' },
    }),
  ),
  videoOrientation: Type.Optional(
    Type.Union(
      [
        Type.Literal('landscape', { label: 'WEBSITE_BUILDER_VIDEO_ORIENTATION_LANDSCAPE' }),
        Type.Literal('portrait', { label: 'WEBSITE_BUILDER_VIDEO_ORIENTATION_PORTRAIT' }),
      ],
      {
        title: 'WEBSITE_BUILDER_TESTIMONIAL_VIDEO_ORIENTATION',
        meta: { multiple: false },
      },
    ),
  ),
  rating: Type.Optional(
    Type.Union(
      [
        Type.Literal(1, { label: '1' }),
        Type.Literal(1.5, { label: '1.5' }),
        Type.Literal(2, { label: '2' }),
        Type.Literal(2.5, { label: '2.5' }),
        Type.Literal(3, { label: '3' }),
        Type.Literal(3.5, { label: '3.5' }),
        Type.Literal(4, { label: '4' }),
        Type.Literal(4.5, { label: '4.5' }),
        Type.Literal(5, { label: '5' }),
      ],
      {
        title: 'WEBSITE_BUILDER_TESTIMONIAL_RATING',
        meta: { multiple: false },
      },
    ),
  ),
});

export const TestimonialsSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_TESTIMONIALS_TITLE',
      placeholder: 'WEBSITE_BUILDER_TESTIMONIALS_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  items: Type.Optional(
    Type.Array(TestimonialItemSchema, {
      title: 'WEBSITE_BUILDER_TESTIMONIALS_ITEMS',
      minItems: 1,
      maxItems: 20,
    }),
  ),
});

export type TestimonialsData = Static<typeof TestimonialsSchema>;
export type TestimonialsVariant = 'carousel' | 'grid' | 'wall' | 'highlight';

export const TestimonialsMeta: ComponentMeta<
  typeof TestimonialsSchema,
  TestimonialsVariant,
  keyof TestimonialsData & string
> = {
  id: COMPONENT_IDS.TESTIMONIALS,
  version: '1.0.0',
  schema: TestimonialsSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    carousel: {
      label: 'WEBSITE_BUILDER_VARIANT_TESTIMONIALS_CAROUSEL',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_TESTIMONIALS_GRID',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
    wall: {
      label: 'WEBSITE_BUILDER_VARIANT_TESTIMONIALS_WALL',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
    highlight: {
      label: 'WEBSITE_BUILDER_VARIANT_TESTIMONIALS_HIGHLIGHT',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
  },
};

// ─── Google Reviews ──────────────────────────────────────────────────
//
// Unlike Testimonials (manually entered), Google reviews are NOT edited in the
// builder: they come from the Google My Business sync (backend, à venir) and a
// professional may have **several GMB sources** (multiple establishments / GBP
// locations). This component is therefore CONFIG-ONLY — the reviews themselves
// are provided at runtime via `prefetchedData` / the public reviews endpoint
// (see PublicGoogleReviewsResponseSchema below for the data contract).

/** Data contract for a single synced Google review (provided at runtime, not edited). */
export const GoogleReviewItemSchema = Type.Object({
  id: Type.String(),
  authorName: Type.String(),
  authorPhotoUrl: Type.Optional(Type.String()),
  rating: Type.Number(),
  text: Type.Optional(Type.String()),
  /** Pre-formatted, localized relative date built by the backend (e.g. "il y a 3 jours"). */
  relativeTimeFormatted: Type.Optional(Type.String()),
  /** Deep-link to the review on Google Maps. */
  reviewUrl: Type.Optional(Type.String()),
  /** Which GMB source (establishment / GBP location) this review came from. */
  sourceId: Type.String(),
  sourceName: Type.Optional(Type.String()),
});
export type GoogleReviewItem = Static<typeof GoogleReviewItemSchema>;

/** Optional aggregate rating per source (overall stars + total count). */
export const GoogleReviewSourceSummarySchema = Type.Object({
  sourceId: Type.String(),
  sourceName: Type.Optional(Type.String()),
  averageRating: Type.Number(),
  totalReviewCount: Type.Number(),
  profileUrl: Type.Optional(Type.String()),
});
export type GoogleReviewSourceSummary = Static<typeof GoogleReviewSourceSummarySchema>;

/** Response returned by the (future) public endpoint and passed as prefetchedData. */
export const PublicGoogleReviewsResponseSchema = Type.Object({
  reviews: Type.Array(GoogleReviewItemSchema),
  summaries: Type.Optional(Type.Array(GoogleReviewSourceSummarySchema)),
});
export type PublicGoogleReviewsResponseObject = Static<typeof PublicGoogleReviewsResponseSchema>;

export const GoogleReviewsSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_GOOGLE_REVIEWS_TITLE',
      placeholder: 'WEBSITE_BUILDER_GOOGLE_REVIEWS_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  // Empty / omitted = every synced GMB source. Otherwise, the GMB source ids to
  // display. Rendered with a dedicated selector once the GMB sync ships; kept
  // `hidden` in the variants meta for now.
  sourceIds: Type.Optional(
    Type.Array(Type.String(), {
      title: 'WEBSITE_BUILDER_GOOGLE_REVIEWS_SOURCES',
      meta: { input_type: 'gmb_source_selector' },
    }),
  ),
  // Only display reviews with at least this rating (filters out low ratings).
  minRating: Type.Optional(
    Type.Union(
      [
        Type.Literal(0, { label: 'WEBSITE_BUILDER_GOOGLE_REVIEWS_MIN_RATING_ALL' }),
        Type.Literal(3, { label: '3+' }),
        Type.Literal(3.5, { label: '3,5+' }),
        Type.Literal(4, { label: '4+' }),
        Type.Literal(4.5, { label: '4,5+' }),
        Type.Literal(5, { label: '5' }),
      ],
      {
        title: 'WEBSITE_BUILDER_GOOGLE_REVIEWS_MIN_RATING',
        default: 4,
        meta: { multiple: false },
      },
    ),
  ),
  maxItems: Type.Optional(
    Type.Union(
      [
        Type.Literal(3, { label: '3' }),
        Type.Literal(6, { label: '6' }),
        Type.Literal(9, { label: '9' }),
        Type.Literal(12, { label: '12' }),
      ],
      {
        title: 'WEBSITE_BUILDER_GOOGLE_REVIEWS_MAX_ITEMS',
        default: 6,
        meta: { multiple: false },
      },
    ),
  ),
  // Show the aggregate rating (overall stars + total review count + Google badge).
  showSummary: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_GOOGLE_REVIEWS_SHOW_SUMMARY',
      default: true,
    }),
  ),
});

export type GoogleReviewsData = Static<typeof GoogleReviewsSchema>;
export type GoogleReviewsVariant = 'carousel' | 'grid' | 'wall';

export const GoogleReviewsMeta: ComponentMeta<
  typeof GoogleReviewsSchema,
  GoogleReviewsVariant,
  keyof GoogleReviewsData & string
> = {
  id: COMPONENT_IDS.GOOGLE_REVIEWS,
  version: '1.0.0',
  schema: GoogleReviewsSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    carousel: {
      label: 'WEBSITE_BUILDER_VARIANT_GOOGLE_REVIEWS_CAROUSEL',
      required: [],
      optional: ['title', 'minRating', 'maxItems', 'showSummary'],
      hidden: ['sourceIds'],
    },
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_GOOGLE_REVIEWS_GRID',
      required: [],
      optional: ['title', 'minRating', 'maxItems', 'showSummary'],
      hidden: ['sourceIds'],
    },
    wall: {
      label: 'WEBSITE_BUILDER_VARIANT_GOOGLE_REVIEWS_WALL',
      required: [],
      optional: ['title', 'minRating', 'maxItems', 'showSummary'],
      hidden: ['sourceIds'],
    },
  },
};

// ─── Before After ────────────────────────────────────────────────────

export const BeforeAfterItemSchema = Type.Object({
  beforeImageUrl: PictureSchema(MEDIUM_SIZES, {
    title: 'WEBSITE_BUILDER_BEFORE_AFTER_BEFORE_IMAGE',
  }),
  afterImageUrl: PictureSchema(MEDIUM_SIZES, {
    title: 'WEBSITE_BUILDER_BEFORE_AFTER_AFTER_IMAGE',
  }),
  caption: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_BEFORE_AFTER_CAPTION',
      placeholder: 'WEBSITE_BUILDER_BEFORE_AFTER_CAPTION_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
});

export const BeforeAfterSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_BEFORE_AFTER_TITLE',
      placeholder: 'WEBSITE_BUILDER_BEFORE_AFTER_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  items: Type.Optional(
    Type.Array(BeforeAfterItemSchema, {
      title: 'WEBSITE_BUILDER_BEFORE_AFTER_ITEMS',
      minItems: 1,
      maxItems: 12,
    }),
  ),
});

export type BeforeAfterData = Static<typeof BeforeAfterSchema>;
export type BeforeAfterVariant = 'slider' | 'side-by-side' | 'gallery';

export const BeforeAfterMeta: ComponentMeta<
  typeof BeforeAfterSchema,
  BeforeAfterVariant,
  keyof BeforeAfterData & string
> = {
  id: COMPONENT_IDS.BEFORE_AFTER,
  version: '1.0.0',
  schema: BeforeAfterSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    slider: {
      label: 'WEBSITE_BUILDER_VARIANT_BEFORE_AFTER_SLIDER',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
    'side-by-side': {
      label: 'WEBSITE_BUILDER_VARIANT_BEFORE_AFTER_SIDE_BY_SIDE',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
    gallery: {
      label: 'WEBSITE_BUILDER_VARIANT_BEFORE_AFTER_GALLERY',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
  },
};

// ─── Specialties ─────────────────────────────────────────────────────

export const SpecialtyItemSchema = Type.Object({
  name: Type.String({
    title: 'WEBSITE_BUILDER_SPECIALTY_NAME',
    placeholder: 'WEBSITE_BUILDER_SPECIALTY_NAME_PLACEHOLDER',
    maxLength: 60,
  }),
  description: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_SPECIALTY_DESCRIPTION',
      placeholder: 'WEBSITE_BUILDER_SPECIALTY_DESCRIPTION_PLACEHOLDER',
      maxLength: 500,
    }),
  ),
  icon: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_SPECIALTY_ICON',
      meta: { input_type: 'icon' },
    }),
  ),
});

export const SpecialtiesSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_SPECIALTIES_TITLE',
      placeholder: 'WEBSITE_BUILDER_SPECIALTIES_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  items: Type.Optional(
    Type.Array(SpecialtyItemSchema, {
      title: 'WEBSITE_BUILDER_SPECIALTIES_ITEMS',
      minItems: 3,
      maxItems: 12,
    }),
  ),
});

export type SpecialtiesData = Static<typeof SpecialtiesSchema>;
export type SpecialtiesVariant = 'grid' | 'list' | 'tags';

export const SpecialtiesMeta: ComponentMeta<
  typeof SpecialtiesSchema,
  SpecialtiesVariant,
  keyof SpecialtiesData & string
> = {
  id: COMPONENT_IDS.SPECIALTIES,
  version: '1.0.0',
  schema: SpecialtiesSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_GRID',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
    list: {
      label: 'WEBSITE_BUILDER_VARIANT_LIST',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
    tags: {
      label: 'WEBSITE_BUILDER_VARIANT_SPECIALTIES_TAGS',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
  },
};

// ─── Promotion ───────────────────────────────────────────────────────

export const PromotionSchema = Type.Object({
  linkedPromotionId: LinkedPromotionIdField,
  // Requis par les variantes, sauf avec une promotion liée (`requiredUnlessSet`).
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PROMOTION_TITLE',
      placeholder: 'WEBSITE_BUILDER_PROMOTION_TITLE_PLACEHOLDER',
      maxLength: 80,
    }),
  ),
  description: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PROMOTION_DESCRIPTION',
      placeholder: 'WEBSITE_BUILDER_PROMOTION_DESCRIPTION_PLACEHOLDER',
      maxLength: 200,
    }),
  ),
  promoCode: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PROMOTION_CODE',
      placeholder: 'WEBSITE_BUILDER_PROMOTION_CODE_PLACEHOLDER',
      maxLength: 30,
    }),
  ),
  expirationDate: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PROMOTION_EXPIRATION',
      format: 'date-time',
      meta: { input_type: 'date' },
    }),
  ),
  ctaLabel: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PROMOTION_CTA_LABEL',
      placeholder: 'WEBSITE_BUILDER_PROMOTION_CTA_LABEL_PLACEHOLDER',
      maxLength: 40,
    }),
  ),
  ctaHref: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PROMOTION_CTA_HREF',
      meta: { field_type: 'link' },
    }),
  ),
  imageUrl: Type.Optional(
    PictureSchema(MEDIUM_SIZES, {
      title: 'WEBSITE_BUILDER_PROMOTION_IMAGE',
    }),
  ),
  // « Offres du moment » : titre et introduction de la section, les cartes viennent des promotions.
  offersTitle: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PROMOTION_OFFERS_TITLE',
      placeholder: 'WEBSITE_BUILDER_PROMOTION_OFFERS_TITLE_PLACEHOLDER',
      maxLength: 80,
    }),
  ),
  offersDescription: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PROMOTION_OFFERS_DESCRIPTION',
      placeholder: 'WEBSITE_BUILDER_PROMOTION_OFFERS_DESCRIPTION_PLACEHOLDER',
      maxLength: 200,
    }),
  ),
});

export type PromotionData = Static<typeof PromotionSchema>;
export type PromotionVariant = 'banner' | 'card' | 'split' | 'current-offers';

/**
 * Variante « Offres du moment » de la section Promotion : liste automatique des promotions mises en avant et
 * actives, une carte chacune. Aucun champ obligatoire ; masquée en public quand il n'y en a aucune.
 */
export const PROMOTION_CURRENT_OFFERS_VARIANT = 'current-offers' satisfies PromotionVariant;

const PROMOTION_OFFERS_FIELDS: (keyof PromotionData & string)[] = ['offersTitle', 'offersDescription'];

export const PromotionMeta: ComponentMeta<typeof PromotionSchema, PromotionVariant, keyof PromotionData & string> = {
  id: COMPONENT_IDS.PROMOTION,
  version: '1.0.0',
  schema: PromotionSchema,
  movable: true,
  allowMultipleAdd: true,
  // Promotion liée : titre, description, code, fin et bouton viennent de la promotion (l'image reste à fournir).
  requiredUnlessSet: { field: LINKED_PROMOTION_FIELD, fields: ['title'] },
  variants: {
    banner: {
      label: 'WEBSITE_BUILDER_VARIANT_PROMOTION_BANNER',
      required: ['title'],
      optional: [LINKED_PROMOTION_FIELD, 'description', 'promoCode', 'expirationDate', 'ctaLabel', 'ctaHref'],
      hidden: ['imageUrl', ...PROMOTION_OFFERS_FIELDS],
    },
    card: {
      label: 'WEBSITE_BUILDER_VARIANT_PROMOTION_CARD',
      required: ['title'],
      optional: [
        LINKED_PROMOTION_FIELD,
        'description',
        'promoCode',
        'expirationDate',
        'ctaLabel',
        'ctaHref',
        'imageUrl',
      ],
      hidden: PROMOTION_OFFERS_FIELDS,
    },
    split: {
      label: 'WEBSITE_BUILDER_VARIANT_PROMOTION_SPLIT',
      required: ['title', 'imageUrl'],
      optional: [LINKED_PROMOTION_FIELD, 'description', 'promoCode', 'expirationDate', 'ctaLabel', 'ctaHref'],
      hidden: PROMOTION_OFFERS_FIELDS,
    },
    [PROMOTION_CURRENT_OFFERS_VARIANT]: {
      label: 'WEBSITE_BUILDER_VARIANT_PROMOTION_CURRENT_OFFERS',
      required: [],
      optional: PROMOTION_OFFERS_FIELDS,
      hidden: [
        LINKED_PROMOTION_FIELD,
        'title',
        'description',
        'promoCode',
        'expirationDate',
        'ctaLabel',
        'ctaHref',
        'imageUrl',
      ],
    },
  },
};

// ─── Photo Gallery ───────────────────────────────────────────────────

export const PhotoGalleryItemSchema = Type.Object({
  imageUrl: PictureSchema(MEDIUM_SIZES, {
    title: 'WEBSITE_BUILDER_PHOTO_GALLERY_IMAGE',
  }),
  caption: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PHOTO_GALLERY_CAPTION',
      placeholder: 'WEBSITE_BUILDER_PHOTO_GALLERY_CAPTION_PLACEHOLDER',
      maxLength: 120,
    }),
  ),
});

export const PhotoGallerySchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PHOTO_GALLERY_TITLE',
      placeholder: 'WEBSITE_BUILDER_PHOTO_GALLERY_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  items: Type.Optional(
    Type.Array(PhotoGalleryItemSchema, {
      title: 'WEBSITE_BUILDER_PHOTO_GALLERY_ITEMS',
      minItems: 1,
      maxItems: 60,
    }),
  ),
});

export type PhotoGalleryData = Static<typeof PhotoGallerySchema>;
export type PhotoGalleryVariant = 'grid' | 'masonry' | 'carousel';

export const PhotoGalleryMeta: ComponentMeta<
  typeof PhotoGallerySchema,
  PhotoGalleryVariant,
  keyof PhotoGalleryData & string
> = {
  id: COMPONENT_IDS.PHOTO_GALLERY,
  version: '1.0.0',
  schema: PhotoGallerySchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_GRID',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
    masonry: {
      label: 'WEBSITE_BUILDER_VARIANT_PHOTO_GALLERY_MASONRY',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
    carousel: {
      label: 'WEBSITE_BUILDER_VARIANT_PHOTO_GALLERY_CAROUSEL',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
  },
};

// ─── Simple Photo ────────────────────────────────────────────────────
// Une seule image avec légende optionnelle — le pendant « unitaire » de la
// galerie, pour poser une photo au milieu d'une page de contenu.

export const SimplePhotoSchema = Type.Object({
  imageUrl: Type.Optional(
    PictureSchema(MEDIUM_SIZES, {
      title: 'WEBSITE_BUILDER_SIMPLE_PHOTO_IMAGE',
    }),
  ),
  caption: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_SIMPLE_PHOTO_CAPTION',
      placeholder: 'WEBSITE_BUILDER_SIMPLE_PHOTO_CAPTION_PLACEHOLDER',
      maxLength: 160,
    }),
  ),
});

export type SimplePhotoData = Static<typeof SimplePhotoSchema>;
export type SimplePhotoVariant = 'standard' | 'small' | 'full';

export const SimplePhotoMeta: ComponentMeta<
  typeof SimplePhotoSchema,
  SimplePhotoVariant,
  keyof SimplePhotoData & string
> = {
  id: COMPONENT_IDS.SIMPLE_PHOTO,
  version: '1.0.0',
  schema: SimplePhotoSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    standard: {
      label: 'WEBSITE_BUILDER_VARIANT_SIMPLE_PHOTO_STANDARD',
      required: ['imageUrl'],
      optional: ['caption'],
      hidden: [],
    },
    small: {
      label: 'WEBSITE_BUILDER_VARIANT_SIMPLE_PHOTO_SMALL',
      required: ['imageUrl'],
      optional: ['caption'],
      hidden: [],
    },
    full: {
      label: 'WEBSITE_BUILDER_VARIANT_SIMPLE_PHOTO_FULL',
      required: ['imageUrl'],
      optional: ['caption'],
      hidden: [],
    },
  },
};

// ─── Methodology ─────────────────────────────────────────────────────

export const MethodologyStepSchema = Type.Object({
  title: Type.String({
    title: 'WEBSITE_BUILDER_METHODOLOGY_STEP_TITLE',
    placeholder: 'WEBSITE_BUILDER_METHODOLOGY_STEP_TITLE_PLACEHOLDER',
    maxLength: 80,
  }),
  description: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_METHODOLOGY_STEP_DESCRIPTION',
      placeholder: 'WEBSITE_BUILDER_METHODOLOGY_STEP_DESCRIPTION_PLACEHOLDER',
      maxLength: 500,
    }),
  ),
  icon: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_METHODOLOGY_STEP_ICON',
      meta: { input_type: 'icon' },
    }),
  ),
});

export const MethodologySchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_METHODOLOGY_TITLE',
      placeholder: 'WEBSITE_BUILDER_METHODOLOGY_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  subtitle: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_METHODOLOGY_SUBTITLE',
      placeholder: 'WEBSITE_BUILDER_METHODOLOGY_SUBTITLE_PLACEHOLDER',
      maxLength: 200,
    }),
  ),
  items: Type.Optional(
    Type.Array(MethodologyStepSchema, {
      title: 'WEBSITE_BUILDER_METHODOLOGY_ITEMS',
      minItems: 3,
      maxItems: 8,
    }),
  ),
});

export type MethodologyData = Static<typeof MethodologySchema>;
export type MethodologyVariant = 'vertical' | 'horizontal' | 'cards';

export const MethodologyMeta: ComponentMeta<
  typeof MethodologySchema,
  MethodologyVariant,
  keyof MethodologyData & string
> = {
  id: COMPONENT_IDS.METHODOLOGY,
  version: '1.0.0',
  schema: MethodologySchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    vertical: {
      label: 'WEBSITE_BUILDER_VARIANT_METHODOLOGY_VERTICAL',
      required: ['items'],
      optional: ['title', 'subtitle'],
      hidden: [],
    },
    horizontal: {
      label: 'WEBSITE_BUILDER_VARIANT_METHODOLOGY_HORIZONTAL',
      required: ['items'],
      optional: ['title', 'subtitle'],
      hidden: [],
    },
    cards: {
      label: 'WEBSITE_BUILDER_VARIANT_METHODOLOGY_CARDS',
      required: ['items'],
      optional: ['title', 'subtitle'],
      hidden: [],
    },
  },
};

// ─── Key Figures ─────────────────────────────────────────────────────

export const KeyFigureItemSchema = Type.Object({
  value: Type.String({
    title: 'WEBSITE_BUILDER_KEY_FIGURE_VALUE',
    placeholder: 'WEBSITE_BUILDER_KEY_FIGURE_VALUE_PLACEHOLDER',
    maxLength: 20,
  }),
  label: Type.String({
    title: 'WEBSITE_BUILDER_KEY_FIGURE_LABEL',
    placeholder: 'WEBSITE_BUILDER_KEY_FIGURE_LABEL_PLACEHOLDER',
    maxLength: 40,
  }),
  icon: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_KEY_FIGURE_ICON',
      meta: { input_type: 'icon' },
    }),
  ),
});

export const KeyFiguresSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_KEY_FIGURES_TITLE',
      placeholder: 'WEBSITE_BUILDER_KEY_FIGURES_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  items: Type.Optional(
    Type.Array(KeyFigureItemSchema, {
      title: 'WEBSITE_BUILDER_KEY_FIGURES_ITEMS',
      minItems: 3,
      maxItems: 6,
    }),
  ),
});

export type KeyFiguresData = Static<typeof KeyFiguresSchema>;
export type KeyFiguresVariant = 'columns' | 'banner' | 'card';

export const KeyFiguresMeta: ComponentMeta<typeof KeyFiguresSchema, KeyFiguresVariant, keyof KeyFiguresData & string> =
  {
    id: COMPONENT_IDS.KEY_FIGURES,
    version: '1.0.0',
    schema: KeyFiguresSchema,
    movable: true,
    allowMultipleAdd: true,
    variants: {
      columns: {
        label: 'WEBSITE_BUILDER_VARIANT_KEY_FIGURES_COLUMNS',
        required: ['items'],
        optional: ['title'],
        hidden: [],
      },
      banner: {
        label: 'WEBSITE_BUILDER_VARIANT_KEY_FIGURES_BANNER',
        required: ['items'],
        optional: [],
        hidden: ['title'],
      },
      card: {
        label: 'WEBSITE_BUILDER_VARIANT_KEY_FIGURES_CARD',
        required: ['items'],
        optional: ['title'],
        hidden: [],
      },
    },
  };

// ─── Quote ───────────────────────────────────────────────────────────

export const QuoteSchema = Type.Object({
  text: Type.String({
    title: 'WEBSITE_BUILDER_QUOTE_TEXT',
    placeholder: 'WEBSITE_BUILDER_QUOTE_TEXT_PLACEHOLDER',
    meta: { input_type: 'rich_text', allowed_tags: ['b', 'i', 'u'], max_chars: 500 },
  }),
  authorName: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_QUOTE_AUTHOR_NAME',
      placeholder: 'WEBSITE_BUILDER_QUOTE_AUTHOR_NAME_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  authorRole: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_QUOTE_AUTHOR_ROLE',
      placeholder: 'WEBSITE_BUILDER_QUOTE_AUTHOR_ROLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  authorPhotoUrl: Type.Optional(
    PictureSchema(AVATAR_SIZES, {
      title: 'WEBSITE_BUILDER_QUOTE_AUTHOR_PHOTO',
    }),
  ),
});

export type QuoteData = Static<typeof QuoteSchema>;
export type QuoteVariant = 'framed' | 'large-text' | 'card';

export const QuoteMeta: ComponentMeta<typeof QuoteSchema, QuoteVariant, keyof QuoteData & string> = {
  id: COMPONENT_IDS.QUOTE,
  version: '1.0.0',
  schema: QuoteSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    framed: {
      label: 'WEBSITE_BUILDER_VARIANT_QUOTE_FRAMED',
      required: ['text'],
      optional: ['authorName', 'authorRole'],
      hidden: ['authorPhotoUrl'],
    },
    'large-text': {
      label: 'WEBSITE_BUILDER_VARIANT_QUOTE_LARGE_TEXT',
      required: ['text'],
      optional: ['authorName', 'authorRole'],
      hidden: ['authorPhotoUrl'],
    },
    card: {
      label: 'WEBSITE_BUILDER_VARIANT_QUOTE_CARD',
      required: ['text'],
      optional: ['authorName', 'authorRole', 'authorPhotoUrl'],
      hidden: [],
    },
  },
};

// ─── Partner Logos ───────────────────────────────────────────────────

export const PartnerLogoItemSchema = Type.Object({
  name: Type.String({
    title: 'WEBSITE_BUILDER_PARTNER_LOGO_NAME',
    placeholder: 'WEBSITE_BUILDER_PARTNER_LOGO_NAME_PLACEHOLDER',
    maxLength: 60,
  }),
  logoUrl: PictureSchema(LOGO_SIZES, {
    title: 'WEBSITE_BUILDER_PARTNER_LOGO_IMAGE',
  }),
  websiteUrl: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PARTNER_LOGO_WEBSITE',
      meta: { field_type: 'link' },
    }),
  ),
});

export const PartnerLogosSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PARTNER_LOGOS_TITLE',
      placeholder: 'WEBSITE_BUILDER_PARTNER_LOGOS_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  items: Type.Optional(
    Type.Array(PartnerLogoItemSchema, {
      title: 'WEBSITE_BUILDER_PARTNER_LOGOS_ITEMS',
      minItems: 2,
      maxItems: 20,
    }),
  ),
});

export type PartnerLogosData = Static<typeof PartnerLogosSchema>;
export type PartnerLogosVariant = 'scrolling' | 'grid';

export const PartnerLogosMeta: ComponentMeta<
  typeof PartnerLogosSchema,
  PartnerLogosVariant,
  keyof PartnerLogosData & string
> = {
  id: COMPONENT_IDS.PARTNER_LOGOS,
  version: '1.0.0',
  schema: PartnerLogosSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    scrolling: {
      label: 'WEBSITE_BUILDER_VARIANT_PARTNER_LOGOS_SCROLLING',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_PARTNER_LOGOS_GRID',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
  },
};

// ─── Certifications ─────────────────────────────────────────────────

export const CertificationItemSchema = Type.Object({
  name: Type.String({
    title: 'WEBSITE_BUILDER_CERTIFICATION_NAME',
    placeholder: 'WEBSITE_BUILDER_CERTIFICATION_NAME_PLACEHOLDER',
    maxLength: 80,
  }),
  imageUrl: Type.Optional(
    PictureSchema(SMALL_SIZES, {
      title: 'WEBSITE_BUILDER_CERTIFICATION_IMAGE',
    }),
  ),
  description: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CERTIFICATION_DESCRIPTION',
      placeholder: 'WEBSITE_BUILDER_CERTIFICATION_DESCRIPTION_PLACEHOLDER',
      maxLength: 200,
    }),
  ),
  year: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CERTIFICATION_YEAR',
      placeholder: 'WEBSITE_BUILDER_CERTIFICATION_YEAR_PLACEHOLDER',
      maxLength: 10,
    }),
  ),
});

export const CertificationsSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_CERTIFICATIONS_TITLE',
      placeholder: 'WEBSITE_BUILDER_CERTIFICATIONS_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  items: Type.Optional(
    Type.Array(CertificationItemSchema, {
      title: 'WEBSITE_BUILDER_CERTIFICATIONS_ITEMS',
      minItems: 1,
      maxItems: 12,
    }),
  ),
});

export type CertificationsData = Static<typeof CertificationsSchema>;
export type CertificationsVariant = 'badges' | 'detailed';

export const CertificationsMeta: ComponentMeta<
  typeof CertificationsSchema,
  CertificationsVariant,
  keyof CertificationsData & string
> = {
  id: COMPONENT_IDS.CERTIFICATIONS,
  version: '1.0.0',
  schema: CertificationsSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    badges: {
      label: 'WEBSITE_BUILDER_VARIANT_CERTIFICATIONS_BADGES',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
    detailed: {
      label: 'WEBSITE_BUILDER_VARIANT_CERTIFICATIONS_DETAILED',
      required: ['items'],
      optional: ['title'],
      hidden: [],
    },
  },
};

// ─── VOD Player (api.video) ─────────────────────────────────────────

export const VodPlayerSchema = Type.Object({
  videoRef: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_VOD_PLAYER_VIDEO_REF',
      placeholder: 'WEBSITE_BUILDER_VOD_PLAYER_VIDEO_REF_PLACEHOLDER',
      meta: { input_type: 'video_ref' },
    }),
  ),
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_VOD_PLAYER_TITLE',
      placeholder: 'WEBSITE_BUILDER_VOD_PLAYER_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  description: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_VOD_PLAYER_DESCRIPTION',
      placeholder: 'WEBSITE_BUILDER_VOD_PLAYER_DESCRIPTION_PLACEHOLDER',
      meta: { input_type: 'rich_text', allowed_tags: ['b', 'i', 'u', 'a'], max_chars: 500 },
    }),
  ),
  autoplay: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_VOD_PLAYER_AUTOPLAY',
      default: false,
    }),
  ),
  muted: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_VOD_PLAYER_MUTED',
      default: false,
    }),
  ),
  loop: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_VOD_PLAYER_LOOP',
      default: false,
    }),
  ),
  controls: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_VOD_PLAYER_CONTROLS',
      default: true,
    }),
  ),
  videoOverlay: Type.Optional(
    Type.Union(
      [
        Type.Literal('none', { label: 'WEBSITE_BUILDER_HERO_VIDEO_OVERLAY_NONE' }),
        Type.Literal('light', { label: 'WEBSITE_BUILDER_HERO_VIDEO_OVERLAY_LIGHT' }),
        Type.Literal('medium', { label: 'WEBSITE_BUILDER_HERO_VIDEO_OVERLAY_MEDIUM' }),
        Type.Literal('strong', { label: 'WEBSITE_BUILDER_HERO_VIDEO_OVERLAY_STRONG' }),
      ],
      {
        title: 'WEBSITE_BUILDER_HERO_VIDEO_OVERLAY',
        default: 'medium',
        meta: { multiple: false },
      },
    ),
  ),
});

export type VodPlayerData = Static<typeof VodPlayerSchema>;
export type VodPlayerVariant = 'full-width' | 'with-text' | 'background-hero';

export const VodPlayerMeta: ComponentMeta<typeof VodPlayerSchema, VodPlayerVariant, keyof VodPlayerData & string> = {
  id: COMPONENT_IDS.VOD_PLAYER,
  version: '1.0.0',
  schema: VodPlayerSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    'full-width': {
      label: 'WEBSITE_BUILDER_VARIANT_VOD_PLAYER_FULL_WIDTH',
      required: ['videoRef'],
      optional: ['title'],
      hidden: ['description', 'autoplay', 'muted', 'loop', 'controls', 'videoOverlay'],
    },
    'with-text': {
      label: 'WEBSITE_BUILDER_VARIANT_VOD_PLAYER_WITH_TEXT',
      required: ['videoRef'],
      optional: ['title', 'description'],
      hidden: ['autoplay', 'muted', 'loop', 'controls', 'videoOverlay'],
    },
    'background-hero': {
      label: 'WEBSITE_BUILDER_VARIANT_VOD_PLAYER_BACKGROUND_HERO',
      required: ['videoRef'],
      optional: ['title', 'description', 'videoOverlay'],
      hidden: ['autoplay', 'muted', 'loop', 'controls'],
    },
  },
};

// ─── Comparison Table ───────────────────────────────────────────────

export const ComparisonColumnSchema = Type.Object({
  title: Type.String({
    title: 'WEBSITE_BUILDER_COMPARISON_COLUMN_TITLE',
    placeholder: 'WEBSITE_BUILDER_COMPARISON_COLUMN_TITLE_PLACEHOLDER',
    maxLength: 40,
  }),
  subtitle: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_COMPARISON_COLUMN_SUBTITLE',
      placeholder: 'WEBSITE_BUILDER_COMPARISON_COLUMN_SUBTITLE_PLACEHOLDER',
      maxLength: 60,
    }),
  ),
  isHighlighted: Type.Optional(
    Type.Boolean({
      title: 'WEBSITE_BUILDER_COMPARISON_COLUMN_HIGHLIGHTED',
      default: false,
    }),
  ),
  ctaLabel: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_COMPARISON_COLUMN_CTA_LABEL',
      placeholder: 'WEBSITE_BUILDER_COMPARISON_COLUMN_CTA_PLACEHOLDER',
      maxLength: 30,
    }),
  ),
  ctaHref: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_COMPARISON_COLUMN_CTA_HREF',
      meta: { field_type: 'link' },
    }),
  ),
});

export const ComparisonRowSchema = Type.Object({
  label: Type.String({
    title: 'WEBSITE_BUILDER_COMPARISON_ROW_LABEL',
    placeholder: 'WEBSITE_BUILDER_COMPARISON_ROW_LABEL_PLACEHOLDER',
    maxLength: 60,
  }),
  values: Type.Array(
    Type.String({
      title: 'WEBSITE_BUILDER_COMPARISON_ROW_VALUE',
      maxLength: 40,
    }),
    {
      title: 'WEBSITE_BUILDER_COMPARISON_ROW_VALUES',
    },
  ),
});

export const ComparisonTableSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_COMPARISON_TABLE_TITLE',
      placeholder: 'WEBSITE_BUILDER_COMPARISON_TABLE_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  columns: Type.Array(ComparisonColumnSchema, {
    title: 'WEBSITE_BUILDER_COMPARISON_TABLE_COLUMNS',
    minItems: 2,
    maxItems: 4,
  }),
  rows: Type.Array(ComparisonRowSchema, {
    title: 'WEBSITE_BUILDER_COMPARISON_TABLE_ROWS',
    minItems: 1,
    maxItems: 20,
  }),
});

export type ComparisonTableData = Static<typeof ComparisonTableSchema>;
export type ComparisonTableVariant = 'table' | 'cards';

export const ComparisonTableMeta: ComponentMeta<
  typeof ComparisonTableSchema,
  ComparisonTableVariant,
  keyof ComparisonTableData & string
> = {
  id: COMPONENT_IDS.COMPARISON_TABLE,
  version: '1.0.0',
  schema: ComparisonTableSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    table: {
      label: 'WEBSITE_BUILDER_VARIANT_COMPARISON_TABLE_TABLE',
      required: ['columns', 'rows'],
      optional: ['title'],
      hidden: [],
    },
    cards: {
      label: 'WEBSITE_BUILDER_VARIANT_COMPARISON_TABLE_CARDS',
      required: ['columns', 'rows'],
      optional: ['title'],
      hidden: [],
    },
  },
};

// ─── News Detail ─────────────────────────────────────────────────────

export const NewsDetailSchema = Type.Object({});

export type NewsDetailData = Static<typeof NewsDetailSchema>;
export type NewsDetailVariant = 'default' | 'sidebar';

export const NewsDetailMeta: ComponentMeta<typeof NewsDetailSchema, NewsDetailVariant, keyof NewsDetailData & string> =
  {
    id: COMPONENT_IDS.NEWS_DETAIL,
    version: '1.0.0',
    schema: NewsDetailSchema,
    movable: false,
    allowMultipleAdd: false,
    variants: {
      default: {
        label: 'WEBSITE_BUILDER_VARIANT_DEFAULT',
        required: [],
        optional: [],
        hidden: [],
      },
      sidebar: {
        label: 'WEBSITE_BUILDER_VARIANT_SIDEBAR',
        required: [],
        optional: [],
        hidden: [],
      },
    },
  };

// ─── Instagram Feed ──────────────────────────────────────────────────

export const InstagramPostItemSchema = Type.Object({
  postUrl: Type.String({
    title: 'WEBSITE_BUILDER_INSTAGRAM_POST_URL',
    placeholder: 'WEBSITE_BUILDER_INSTAGRAM_POST_URL_PLACEHOLDER',
    pattern: '^https?://(www\\.)?instagram\\.com/(p|reel|tv)/[A-Za-z0-9_-]+/?.*$',
    meta: { field_type: 'link' },
  }),
});

export const InstagramFeedSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_INSTAGRAM_FEED_TITLE',
      placeholder: 'WEBSITE_BUILDER_INSTAGRAM_FEED_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  handle: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_INSTAGRAM_FEED_HANDLE',
      placeholder: 'WEBSITE_BUILDER_INSTAGRAM_FEED_HANDLE_PLACEHOLDER',
      maxLength: 40,
    }),
  ),
  items: Type.Optional(
    Type.Array(InstagramPostItemSchema, {
      title: 'WEBSITE_BUILDER_INSTAGRAM_FEED_ITEMS',
      minItems: 1,
      maxItems: 12,
    }),
  ),
});

export type InstagramFeedData = Static<typeof InstagramFeedSchema>;
export type InstagramFeedVariant = 'grid';

export const InstagramFeedMeta: ComponentMeta<
  typeof InstagramFeedSchema,
  InstagramFeedVariant,
  keyof InstagramFeedData & string
> = {
  id: COMPONENT_IDS.INSTAGRAM_FEED,
  version: '1.0.0',
  schema: InstagramFeedSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_INSTAGRAM_FEED_GRID',
      required: ['items'],
      optional: ['title', 'handle'],
      hidden: [],
    },
  },
};

// ─── Cookie Policy Details ───────────────────────────────────────────

export const CookiePolicyDetailsSchema = Type.Object({});

export type CookiePolicyDetailsData = Static<typeof CookiePolicyDetailsSchema>;
export type CookiePolicyDetailsVariant = 'default';

export const CookiePolicyDetailsMeta: ComponentMeta<
  typeof CookiePolicyDetailsSchema,
  CookiePolicyDetailsVariant,
  keyof CookiePolicyDetailsData & string
> = {
  id: COMPONENT_IDS.COOKIE_POLICY_DETAILS,
  version: '1.0.0',
  schema: CookiePolicyDetailsSchema,
  movable: true,
  allowMultipleAdd: false,
  variants: {
    default: {
      label: 'WEBSITE_BUILDER_VARIANT_DEFAULT',
      required: [],
      optional: [],
      hidden: [],
    },
  },
};

// ─── Area List ───────────────────────────────────────────────────────
//
// Composant du hub Lieux : liste dynamique des zones d'intervention du pro
// (city/department/region). Les données sont fetched côté SSR depuis
// `service_areas`. Le composant ne stocke que les options d'affichage.

export const AreaListSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_AREA_LIST_TITLE',
      placeholder: 'WEBSITE_BUILDER_AREA_LIST_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
  /**
   * Zones (area_keys) sur lesquelles on publie une sous-page `/lieux/[slug]`.
   * Vide / undefined = sentinelle "toutes les zones" (cf. `use_all_sentinel`,
   * même pattern que `selectedPlaceIds` sur ContactInfo).
   * Pilote (a) l'affichage dans le listing AreaList et (b) le routing fille +
   * sitemap côté `custom-websites`.
   */
  selectedAreaKeys: Type.Optional(
    Type.Array(Type.String(), {
      title: 'WEBSITE_BUILDER_AREA_LIST_SELECTED_AREAS',
      placeholder: 'WEBSITE_BUILDER_AREA_LIST_SELECTED_AREAS',
      meta: { input_type: 'service_area_selector', use_all_sentinel: true },
    }),
  ),
});

export type AreaListData = Static<typeof AreaListSchema>;
export type AreaListVariant = 'grid' | 'list';

export const AreaListMeta: ComponentMeta<typeof AreaListSchema, AreaListVariant, keyof AreaListData & string> = {
  id: COMPONENT_IDS.AREA_LIST,
  version: '1.0.0',
  schema: AreaListSchema,
  movable: true,
  allowMultipleAdd: false,
  variants: {
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_AREA_LIST_GRID',
      required: [],
      optional: ['title', 'selectedAreaKeys'],
      hidden: [],
    },
    list: {
      label: 'WEBSITE_BUILDER_VARIANT_AREA_LIST_LIST',
      required: [],
      optional: ['title', 'selectedAreaKeys'],
      hidden: [],
    },
  },
};

// ─── Sub-Pages List ──────────────────────────────────────────────────
// Liste dynamique des sous-pages (`children`) de la page courante. Aucune donnée
// stockée hormis le titre : les entrées sont dérivées de l'arbre des pages, donc
// l'ajout/suppression d'une sous-page se reflète automatiquement.

export const SubPagesListSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_SUB_PAGES_LIST_TITLE',
      placeholder: 'WEBSITE_BUILDER_SUB_PAGES_LIST_TITLE_PLACEHOLDER',
      maxLength: 100,
    }),
  ),
});

export type SubPagesListData = Static<typeof SubPagesListSchema>;
export type SubPagesListVariant = 'cards' | 'list' | 'grid';

export const SubPagesListMeta: ComponentMeta<
  typeof SubPagesListSchema,
  SubPagesListVariant,
  keyof SubPagesListData & string
> = {
  id: COMPONENT_IDS.SUB_PAGES_LIST,
  version: '1.0.0',
  schema: SubPagesListSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    cards: {
      label: 'WEBSITE_BUILDER_VARIANT_SUB_PAGES_LIST_CARDS',
      required: [],
      optional: ['title'],
      hidden: [],
    },
    list: {
      label: 'WEBSITE_BUILDER_VARIANT_SUB_PAGES_LIST_LIST',
      required: [],
      optional: ['title'],
      hidden: [],
    },
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_SUB_PAGES_LIST_GRID',
      required: [],
      optional: ['title'],
      hidden: [],
    },
  },
};

// ─── Pack Listing (Forfaits) ─────────────────────────────────────────

export const PackListingSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PACK_LISTING_TITLE',
    }),
  ),
});

export type PackListingData = Static<typeof PackListingSchema>;
export type PackListingVariant = 'grid' | 'list';

export const PackListingMeta: ComponentMeta<
  typeof PackListingSchema,
  PackListingVariant,
  keyof PackListingData & string
> = {
  id: COMPONENT_IDS.PACK_LISTING,
  version: '1.0.0',
  schema: PackListingSchema,
  movable: true,
  allowMultipleAdd: false,
  variants: {
    grid: {
      label: 'WEBSITE_BUILDER_VARIANT_GRID',
      required: [],
      optional: ['title'],
      hidden: [],
    },
    list: {
      label: 'WEBSITE_BUILDER_VARIANT_LIST',
      required: [],
      optional: ['title'],
      hidden: [],
    },
  },
};
